import React from 'react';
import classNames from 'classnames';

import CustomScrollTrackerModule from '@shcc/modules/CustomScrollTrackerModule';
import { resizeModule } from '@shcc/modules/ResizeModule';
import { adaptiveModule } from '@shcc/modules/AdaptiveModule';

// Utils
import bindAll from '@shcc/utils/helpers/bindAll';
import clearProps from '@shcc/utils/helpers/clearProps';

// Styles
import styles from './SectionContainerComponent.module.scss';

class SectionContainerComponent extends React.Component {
  constructor() {
    super();

    bindAll(this, [
      '_trackerUpdateProgressHandler',
      '_trackerUpdateVisibilityHandler',
      '_resizeHandler',
    ]);

    this.element = React.createRef();
  }

  componentDidMount() {
    const { tracker } = this.props;
    if (tracker && tracker.container) {
      this._setSize();
      this._setupTracker();
      this._setupTrackerTweens();
      this._updateTrackerTweens();
      this._setupTrackerEventListeners();
    }
  }

  componentWillUnmount() {
    const { tracker } = this.props;
    if (tracker && tracker.container) {
      this._removeTrackerEventListeners();
      this._removeTrackerTweens();
      this._removeTracker();
    }
  }

  render() {
    const { className, color, style } = this.props;

    return (
      <div
        className={classNames(styles.container, className)}
        ref={this.element}
        style={color ? { backgroundColor: color, ...style } : style}
      >
        {this.props.children}
      </div>
    );
  }

  getElement() {
    return this.element.current;
  }

  _setSize() {
    const { fill } = this.props;
    const element = this.element.current;

    // prettier-ignore
    if (fill) {
      // Reset size
      element.removeAttribute('style');

      const height = Math.max(resizeModule.viewportHeight, element.clientHeight);
      const width = Math.max(resizeModule.viewportWidth, element.clientWidth);

      element.style.height = `${height}px`;
      element.style.width = `${width}px`;
    }
  }

  _setupTracker() {
    const { tracker } = this.props;
    // const context = scrollContainerModule.get(tracker.container);

    this._tracker = new CustomScrollTrackerModule({
      element: this.getElement(),
      container: tracker.container,
    });
  }

  _removeTracker() {
    if (this._tracker) {
      this._tracker.destroy();
      this._tracker = null;
    }
  }

  _setupTrackerTweens() {
    const { tracker } = this.props;

    this._tweens = [];

    if (tracker && tracker.tweens) {
      this._setupTweensProgress();
      this._setupTweensTrigger();
    }
  }

  // prettier-ignore
  _setupTweensProgress() {
    const { tracker } = this.props;
    const tweens = tracker.tweens || [];
    const limit = tweens.length;
    const adaptive = adaptiveModule.getActiveMediaQuery();

    let index;
    let tween;

    for (index = 0; index < limit; index++) {
      tween = tweens[index];

      if (tween.type === 'progress' && !tween.adaptive) {
        tween = tween.getTween(this._tracker);
        tween.addition = {};
        tween.addition.type = 'progress';

        this._tweens.push(tween);
      }

      if (tween.type === 'progress' && tween.adaptive && tween.adaptive.indexOf(adaptive) >= 0) {
        tween = tween.getTween(this._tracker);
        tween.progress(this._tracker.progress);
        tween.addition = {};
        tween.addition.type = 'progress';

        this._tweens.push(tween);
      }
    }
  }

  // prettier-ignore
  _setupTweensTrigger() {
    const { tracker } = this.props;
    const tweens = tracker.tweens || [];
    const limit = tweens.length;
    const adaptive = adaptiveModule.getActiveMediaQuery();

    let index;
    let tween;
    let progress;

    for (index = 0; index < limit; index++) {
      tween = tweens[index];

      if (tween.type === 'trigger' && !tween.adaptive) {
        progress = tween.progress;

        tween = tween.getTween(this._tracker);

        tween.addition = {};
        tween.addition.type = 'trigger';
        tween.addition.progress = progress;
        tween.addition.triggered = false;

        this._tweens.push(tween);
      }

      if (tween.type === 'trigger' && tween.adaptive && tween.adaptive.indexOf(adaptive) >= 0) {
        progress = tween.progress;

        tween = tween.getTween(this._tracker);

        tween.addition = {};
        tween.addition.type = 'trigger';
        tween.addition.progress = progress;
        tween.addition.triggered = false;

        this._tweens.push(tween);
      }
    }
  }

  // prettier-ignore
  _updateTrackerTweens() {
    const limit = this._tweens.length;

    let index;
    let tween;

    for (index = 0; index < limit; index++) {
      tween = this._tweens[index];

      /**
       *
       * Update progress tween
       */
      if (tween.addition.type === 'progress') {
        tween.progress(this._tracker.progress);
      }

      /**
       *
       * Update trigger tween reset
       */
      if (tween.addition.type === 'trigger' && tween.addition.triggered === true && this._tracker.progress === 0.00) {
        tween.addition.triggered = false;
        tween.pause(0);
      }

      /**
       *
       * Update trigger tween play
       */
      if (tween.addition.type === 'trigger' && tween.addition.triggered === false && this._tracker.progress >= tween.addition.progress) {
        tween.addition.triggered = true;
        tween.play(0);
      }
    }
  }

  _removeTrackerTweens() {
    const limit = this._tweens.length;

    let index;
    let tween;

    for (index = 0; index < limit; index++) {
      tween = this._tweens[index];

      // Clear every timeline prop
      clearProps(tween);

      // Clean up
      tween.kill();
      tween = null;
    }

    this._tweens = [];
  }

  _setupTrackerEventListeners() {
    this._tracker.addEventListener(
      'update:progress',
      this._trackerUpdateProgressHandler
    );
    this._tracker.addEventListener(
      'update:visibility',
      this._trackerUpdateVisibilityHandler
    );

    resizeModule.addEventListener('resize', this._resizeHandler);
    resizeModule.addEventListener('resize:complete', this._resizeHandler);
  }

  _removeTrackerEventListeners() {
    this._tracker.removeEventListener(
      'update:progress',
      this._trackerUpdateProgressHandler
    );
    this._tracker.removeEventListener(
      'update:visibility',
      this._trackerUpdateVisibilityHandler
    );

    resizeModule.removeEventListener('resize', this._resizeHandler);
    resizeModule.removeEventListener('resize:complete', this._resizeHandler);
  }

  /**
   *
   * Handlers
   */
  _resizeHandler() {
    this._setSize();
    this._removeTrackerTweens();
    this._setupTrackerTweens();
    this._updateTrackerTweens();
  }

  _trackerUpdateProgressHandler(payload) {
    const { tracker } = this.props;
    const { onUpdateProgress } = tracker;
    if (onUpdateProgress) onUpdateProgress.call(this, payload);
    this._updateTrackerTweens();
  }

  _trackerUpdateVisibilityHandler(payload) {
    const { tracker } = this.props;
    const { onUpdateVisibility } = tracker;
    if (onUpdateVisibility) onUpdateVisibility.call(this, payload);

    // this.getElement().style.visibility = !payload.visible
    //   ? 'hidden'
    //   : 'visible';
  }
}

export default SectionContainerComponent;
